import React, { useMemo } from "react";
import {
  Tabs,
  Tree,
  Typography,
  Space,
  Button,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  SelectOutlined,
  FolderOutlined,
  FileTextOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { exportNote } from "./api/api";
import DataTable from "../../../components/DataTable";

const downloadJSON = (data, filename) => {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function SkeyenoteNotesTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
  onDeleteNote,
  messageApi,
  isRoleUser,
}) {
  const env = window._env_ || {};
  const skeyenotePort = env.SKEYENOTE_PORT || "8888";
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  const skeyenoteUrl =
    env.SKEYENOTE_URL || `${protocol}//${host}:${skeyenotePort}`;

  const handleExportNote = async (record) => {
    try {
      messageApi.loading({
        content: `Exporting note ${record.id}...`,
        key: "export",
      });
      const response = await exportNote(record.id);
      downloadJSON(response.data, `note_${record.id}.json`);
      messageApi.success({
        content: "Note exported successfully",
        key: "export",
      });
    } catch (error) {
      const backendMessage =
        error.response?.data?.error ||
        error.response?.data?.exception ||
        error.response?.data?.message ||
        error.message ||
        "Failed to export Note";
      messageApi.error({ content: backendMessage, key: "export-note-error" });
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "25%",
      sorter: (a, b) => (a.id || "").localeCompare(b.id || ""),
    },
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      width: "50%",
      sorter: (a, b) => (a.path || "").localeCompare(b.path || ""),
    },
    {
      title: "Actions",
      key: "actions",
      width: "25%",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Open in Skeyenote">
            <Button
              type="text"
              href={`${skeyenoteUrl}/#/notebook/${record.id}`}
              target="_blank"
              rel="noopener noreferrer"
              icon={<SelectOutlined />}
              style={{ color: "#1890ff" }}
            />
          </Tooltip>
          <Tooltip title="Export JSON">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              style={{ color: "#52c41a" }}
              onClick={() => handleExportNote(record)}
            />
          </Tooltip>
          {!isRoleUser && (
            <Popconfirm
              title="Delete Note"
              description="Are you sure you want to delete this note?"
              onConfirm={() => onDeleteNote(record.id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete Note">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const treeData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const tree = {};
    data.forEach((note) => {
      if (!note.path) return;
      const parts = note.path.split("/").filter((p) => p.trim() !== "");
      let currentLevel = tree;
      parts.forEach((part, index) => {
        if (!currentLevel[part]) {
          currentLevel[part] = {
            key: parts.slice(0, index + 1).join("/"),
            title: part,
            children: {},
            noteData: index === parts.length - 1 ? note : null,
          };
        }
        currentLevel = currentLevel[part].children;
      });
    });
    const buildArray = (obj) =>
      Object.values(obj).map((item) => {
        const isLeaf = Object.keys(item.children).length === 0;
        return {
          key: item.key,
          title: item.title,
          data: item.noteData,
          isLeaf,
          icon: isLeaf ? (
            <FileTextOutlined style={{ color: "#1890ff" }} />
          ) : (
            <FolderOutlined style={{ color: "#faad14" }} />
          ),
          children: isLeaf ? undefined : buildArray(item.children),
        };
      });
    return buildArray(tree);
  }, [data]);

  const items = [
    {
      key: "1",
      label: (
        <span>
          <FileTextOutlined /> Data Table
        </span>
      ),
      children: (
        <DataTable
          rowKey="id"
          columns={columns}
          data={data}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          sticky
          pageSizeOptions={[10, 25, 50, 100]}
        />
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <ApartmentOutlined /> Tree View
        </span>
      ),
      children:
        treeData.length > 0 ? (
          <div
            style={{
              padding: "16px",
              backgroundColor: "var(--surface, #fff)",
              border: "1px solid var(--blue-100, transparent)",
              borderRadius: "8px",
            }}
          >
            <Tree treeData={treeData} showIcon defaultExpandAll />
          </div>
        ) : (
          <Typography.Text type="secondary">
            No structured tree data found.
          </Typography.Text>
        ),
    },
  ];

  return <Tabs defaultActiveKey="1" items={items} />;
}
